import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import ContentBlocks from '../../components/ContentBlocks';
import { Button } from '../../components/ui/button';
import Inps499ProgramReview from './Inps499ProgramReview';
import Inps499BandoReview from './Inps499BandoReview';
import Inps499BatchReview from './Inps499BatchReview';

const BASE = '/admin/direct-import/inps499';
const checks = {
  all_claims_checked_against_sources: 'Ho verificato tutte le affermazioni della lezione confrontandole con le fonti associate.',
  normative_references_checked: 'Ho verificato riferimenti normativi, pertinenza e aggiornamento applicabili a questa unità.',
  quiz_answers_and_explanations_checked: 'Ho controllato domande, risposta corretta e spiegazioni di tutti i quiz dell’unità.',
  scheme_checked: 'Ho verificato lo schema, i collegamenti e la coerenza con la lezione.',
};
const blankChecks = () => Object.fromEntries(Object.keys(checks).map(k => [k, false]));

export default function AdminInps499Import() {
  const [report, setReport] = useState(null);
  const [sid, setSid] = useState('');
  const [unit, setUnit] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');
  const [verdict, setVerdict] = useState('');
  const [attestations, setAttestations] = useState(blankChecks);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(BASE);
      setReport(data); setSid(prev => prev || data.units[0]?.section_id || '');
    } catch (e) { setError(e.response?.status === 404 ? 'Il pacchetto non è ancora importato sul backend.' : 'Impossibile leggere lo stato dell’importazione. Nessuna approvazione è stata registrata.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!sid) return;
    let active = true;
    setUnit(null); setReason(''); setVerdict(''); setAttestations(blankChecks()); setMessage('');
    api.get(`${BASE}/units/${encodeURIComponent(sid)}`).then(({ data }) => {
      if (active) setUnit(data);
    }).catch(() => { if (active) setError('Unità non disponibile: riprovare prima di registrare una revisione.'); });
    return () => { active = false; };
  }, [sid]);
  async function recordReview(event) {
    event.preventDefault();
    if (!unit || !verdict || reason.trim().length < 30 || !Object.values(attestations).every(Boolean)) return;
    setBusy(true); setError('');
    try {
      await api.post(`${BASE}/units/${encodeURIComponent(sid)}/review`, {
        expected_unit_sha256: unit.unit_sha256, verdict, reason: reason.trim(), ...attestations,
      });
      setMessage('Revisione dell’unità registrata con il tuo account. Il corso resta nascosto e non acquistabile. Non equivale all’approvazione finale.');
      await load();
    } catch { setError('Revisione non registrata: verifica che l’unità non sia cambiata e che tutte le attestazioni siano complete.'); }
    finally { setBusy(false); }
  }
  return <div className="space-y-6" data-testid="inps499-import-review">
    <header className="surface p-5">
      <p className="eyebrow">Importazione diretta · revisione autenticata</p>
      <h1 className="font-display font-bold text-2xl text-brand-dark">INPS – 499 Assistenti informatici</h1>
      <p className="mt-3 text-sm">Anteprima editoriale privata con i componenti del reader ConcorsiEssentials. Le fonti sono consultabili per la verifica: la loro presenza non costituisce autorizzazione all’uso studente.</p>
      <p className="mt-3 font-semibold text-amber-800">Corso nascosto · non acquistabile · pubblicazione non disponibile in questa pagina</p>
    </header>
    {error && <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-800">{error}</p>}
    {message && <p role="status" className="rounded-xl bg-green-50 p-4 text-green-800">{message}</p>}
    {!report && !error && <p role="status">Caricamento dello stato verificato…</p>}
    {report && <>
      <section className="surface p-5 space-y-3">
        <h2 className="font-semibold text-lg">Stato e controlli</h2>
        <p>{report.counts.units}/44 unità · {report.counts.quizzes}/274 quiz · {report.counts.schemes}/44 schemi</p>
        <p>{report.counts.requirements}/16 voci di programma associate · {report.counts.references} riferimenti · {report.counts.sources} fonti</p>
        <p>Revisioni autenticate delle unità: {report.units.filter(u => u.reviewed).length}/44. Integrità tecnica: {report.technical_integrity ? 'verificata' : 'da correggere'}.</p>
        <details><summary className="cursor-pointer font-medium">Gate ancora da completare ({report.blocking_reasons.length})</summary>
          <ul className="list-disc pl-5 mt-3 text-sm break-words">{report.blocking_reasons.map(b => <li key={b}>{b}</li>)}</ul>
        </details>
        <details><summary className="cursor-pointer font-medium">Programma e associazioni</summary>
          <ul className="list-disc pl-5 mt-3">{report.program.scope_requirements.map(r => <li key={r.requirement_id} className="mb-2">{r.quote} — {r.locator} ({report.coverage[r.requirement_id]?.length || 0} unità)</li>)}</ul>
        </details>
        {!!report.source_evidence_limitations?.length && <details><summary className="cursor-pointer font-medium">Limiti delle evidenze archiviate</summary>
          <ul className="list-disc pl-5 mt-3 text-sm">{report.source_evidence_limitations.map(s => <li key={s.source_id}>{s.source_id}: {s.kind}. {s.note}</li>)}</ul>
        </details>}
        <p className="text-sm">Programma e fonti mantengono i controlli esistenti: <Link className="text-brand underline" to="/admin/pipeline">programma e autorizzazioni delle fonti</Link> · <Link className="text-brand underline" to="/admin/learning-graph">fonti e riferimenti</Link>. Nessun controllo viene approvato automaticamente.</p>
      </section>
      <Inps499ProgramReview report={report} onSaved={load} />
      <Inps499BatchReview onSaved={load} />
      {!report.blocking_reasons.includes('SCOPE_APPROVED_BANDO_REQUIRED') && <Inps499BandoReview onSaved={load} />}
      <label className="block font-medium">Unità da leggere e revisionare
        <select className="mt-2 w-full rounded-lg border p-3 bg-white" value={sid} onChange={e => setSid(e.target.value)}>
          {report.units.map((u,i) => <option key={u.section_id} value={u.section_id}>{i+1}. {u.title}{u.reviewed ? ' · revisione registrata' : ''}</option>)}
        </select>
      </label>
    </>}
    {unit && <>
      <article className="surface p-5 sm:p-8 min-w-0 overflow-x-auto">
        <h2 className="font-display font-bold text-2xl mb-7">{unit.section.title}</h2>
        <ContentBlocks blocks={unit.section.blocks} editorialBlocks={unit.section.editorial_blocks} sources={unit.sources}
          onSectionSelect={target => { if (report.units.some(u => u.section_id === target)) setSid(target); }} />
      </article>
      <section className="surface p-5 space-y-4"><h2 className="text-xl font-semibold">Fonti e approfondimenti dell’unità</h2>
        {unit.references.map(ref => {
          const source = unit.sources.find(s => s.source_id === ref.source_id);
          return <div key={ref.reference_id}><p className="font-medium">{ref.reference_label}</p>
            <p className="text-sm text-slate-600">{ref.anchor}</p>
            {source && <a className="text-brand underline break-words" href={source.official_url} target="_blank" rel="noopener noreferrer">{source.title} — {source.issuer} ↗</a>}
          </div>;
        })}
      </section>
      <section className="surface p-5 space-y-6"><h2 className="text-xl font-semibold">Quiz, soluzioni e spiegazioni ({unit.questions.length})</h2>
        {unit.questions.map((q,i) => <div key={q.question_id}><h3 className="font-semibold">{i+1}. {q.text}</h3>
          <ol className="list-[upper-alpha] pl-6 my-3">{q.options.map((o,n) => <li key={n}>{o}</li>)}</ol>
          <details><summary className="cursor-pointer text-brand">Soluzione e commento</summary><p className="mt-2">Risposta {String.fromCharCode(65+q.correct_index)}: {q.explanation}</p></details>
        </div>)}
      </section>
      <form onSubmit={recordReview} className="surface p-5 space-y-4">
        <h2 className="text-xl font-semibold">La tua revisione di questa unità</h2>
        <p className="text-sm">Compila personalmente dopo la verifica. Nessun campo è preselezionato. La decisione è associata all’impronta esatta dei contenuti e al tuo account.</p>
        {Object.entries(checks).map(([key,label]) => <label key={key} className="flex gap-3 items-start text-sm"><input type="checkbox" className="mt-1" checked={attestations[key]} onChange={e => setAttestations({...attestations,[key]:e.target.checked})}/>{label}</label>)}
        <label className="block">Esito<select value={verdict} onChange={e => setVerdict(e.target.value)} className="block w-full border rounded-lg p-3 mt-2" required>
          <option value="">Seleziona personalmente un esito</option><option value="SUPPORTED">Verificata e supportata dalle fonti</option><option value="UNSUPPORTED">Affermazioni non supportate</option><option value="CONFLICTING">Contraddizioni da risolvere</option><option value="REQUIRES_HUMAN_REVIEW">Ulteriori verifiche necessarie</option>
        </select></label>
        <label className="block">Motivazione concreta<textarea value={reason} onChange={e => setReason(e.target.value)} minLength={30} required className="block border rounded-lg p-3 w-full mt-2" rows={4} /></label>
        <Button type="submit" disabled={busy || !verdict || reason.trim().length < 30 || !Object.values(attestations).every(Boolean)}>{busy ? 'Registrazione…' : 'Registra la revisione di questa unità'}</Button>
      </form>
    </>}
  </div>;
}
